/**
 * Premium Selection section page (OR-1241..1244) — the Calculations
 * group's premium walk. Replaces the old "Quote" WIP entry.
 *
 * Layout mirrors Loss Runs: header (title + dates) → summary cards →
 * two-column card grid (left: inputs/calculations, right: reference) →
 * full-width Approval Notes card (which now also holds the Referral &
 * Approval dates, side by side) → Save & Continue footer.
 *
 * The derived totals are computed SYNCHRONOUSLY on the FE (manual-input
 * model, no recalc endpoint — see `use-premium-selection.ts`) off real
 * upstream refs (Schedule + Loss Runs). Persistence is the real Quote
 * backend (PUT/GET `/v2/submissions/{id}/quote/`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowRight, Calendar, Clipboard, List } from "@untitledui/icons";
import {
    ApiError,
    calculateSurplusLinesTax,
    createMiscFee,
    deleteMiscFee,
    getPremiumSelection,
    getState,
    patchSubmission,
    savePremiumSelection,
} from "../../../api";
import { fractionToPctInput } from "../../../api/adapters/quote";
import {
    computeDeviationFromUnity,
    computeFees,
    computeTotalCommissionPct,
    inferCombinedTaxableBaseIncludesFee,
    inferTaxableBaseIncludesFee,
    resolveSurplusLinesProvenance,
} from "../../../domain/pricing";
import type { PremiumSelectionDerived } from "../../../domain/pricing";
import type { MiscFee } from "../../../types/misc-fee";
import { CustomFeeSaveError, sumMiscFeeAmounts } from "../../../types/misc-fee";
import { useDebouncedAutosave } from "../../../hooks/use-debounced-autosave";
import { useFacilities } from "../../../hooks/use-facilities";
import { FeaturedAlert } from "../../../components/featured-alert";
import { SubmissionFooter } from "../../../components/submission-footer";
import type { SubmissionForm } from "../../../types/submission";
import { useSubmissionContext } from "../context";
import { getNextSection } from "../sections";
import { CheckboxListCard } from "./components/checkbox-list-card";
import type { TaxCalcPhase } from "./components/inscipher-row";
import { FORTY_PCT_CRITERIA, REFERRAL_CRITERIA, emptyCriteriaState } from "./premium-selection-types";
import { AdditionalCoveragesCard } from "./sections/additional-coverages-card";
import { ApprovalNotesCard } from "./sections/approval-notes-card";
import { CommissionsCard } from "./sections/commissions-card";
import { PremiumCard } from "./sections/premium-card";
import { TaxesFeesCard } from "./sections/taxes-fees-card";
import { PolicyTermsCard } from "./sections/policy-terms-card";
import { CoverageBCard, CoverageCCard } from "./sections/coverage-limits-cards";
import { PrimaryPremiumWalkCard } from "./sections/primary-premium-walk-card";
import { ReferralApprovalDatesCard } from "./sections/referral-approval-dates-card";
import { SirCreditsCard } from "./sections/sir-credits-card";
import { PremiumSummaryCards } from "./sections/summary-cards";
import { usePremiumSelection, type InputKey } from "./use-premium-selection";
import { countMissingRetention, hidePartialSirValues } from "./missing-retention";

const SECTION_ID = "premium-selection";

/** Save-signature fingerprint of the misc fee lines: id + amount per row,
 *  so a create/delete (or an amount change) counts as unsaved work. */
function miscFeesSig(fees: readonly MiscFee[]): [string, number | null][] {
    return fees.map((f) => [f.id, f.amount]);
}

/** Whether the sections that feed the premium calc are filled out.
 *  TODO(phase 2): replace this proxy with real completeness flags —
 *  the backend doesn't expose them yet, so we approximate with the
 *  account dates that every filled submission has. */
function arePrereqSectionsComplete(detail: SubmissionForm): boolean {
    return Boolean(detail.accountBasics.renewalDate && detail.accountBasics.expirationDate);
}

export function PremiumSelectionPage() {
    const { detail, setDetail } = useSubmissionContext();
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    /** The archived-coverage alert is dismissible (OR-1431). Session-only: it
     *  comes back on the next visit, since the premium is still being
     *  calculated without those facilities. */
    const [archivedAlertDismissed, setArchivedAlertDismissed] = useState(false);
    // Gates the debounced auto-save: stays false until the saved quote is
    // loaded so hydrating the form isn't mistaken for a user edit.
    const [loaded, setLoaded] = useState(false);
    // Signature of the last-persisted state — drives the footer's dirty
    // flag (Save enabled / Continue guard). Captured on load + after every
    // save (manual or debounced).
    const [baselineSig, setBaselineSig] = useState<string | null>(null);

    const { inputs, setInput, setAndCommit, setInputIfEmpty, commit, hydrate, hydrateDerived, derived, pending } = usePremiumSelection();

    const { state: facilitiesState } = useFacilities(detail.id);
    /**
     * Facility counts for the archived-coverage alert.
     *
     * Archived facilities are left out of coverage (OR-1431): the design states
     * an archived facility is "excluded from coverage and the premium
     * calculation below". The Tech/RM Fee used to be derived from this count
     * here too; since OR-1501 the backend owns that derivation.
     */
    const { totalFacilities, coveredFacilities, missingRetention } = useMemo(() => {
        const all = facilitiesState.status === "ready" ? facilitiesState.data : [];
        return {
            totalFacilities: all.length,
            coveredFacilities: all.filter((f) => !f.isExcluded).length,
            // Only counted once facilities are ready, so the alert never flashes while loading (OR-1733).
            missingRetention: countMissingRetention(all),
        };
    }, [facilitiesState]);
    const archivedFacilities = totalFacilities - coveredFacilities;

    // The Tech/RM Fee on screen. Server-derived by default — the backend
    // recomputes covered facilities × $5,000 on every location change and
    // every quote write, so the stored value is what the quote, binder and dec
    // print (OR-1501) — but editable since backend PR #371: not every facility on a
    // policy carries the Gemini service (a real pricing model had 32 SNFs and
    // a $30,000 fee), so the underwriter can type the fee over the derived
    // one. Once they do, the backend stops restating it.
    const techRmFeeAmount = Number(inputs.techRmFee) || 0;
    /**
     * Tech/RM Fee ownership (backend PR #371) — same shape as the surplus-lines
     * provenance below.
     *
     * `serverTechRmFee` is the fee as the quote last handed it to us (load and
     * post-save re-GET). The PUT sends `tech_rm_fee` only while the
     * input has drifted from it: the backend flips `tech_rm_fee_manually_set`
     * on any CHANGED value, so echoing the loaded fee back on every autosave
     * would be harmless today but would make the page look like it owns a
     * number it never typed — and an echo of a value the backend re-derived
     * between our load and our save WOULD count as a change and freeze it.
     *
     * No-quote path: null until a quote exists to load a baseline from. The
     * input then holds the seeded default ("0"), which nobody typed, so only a
     * fee the underwriter actually committed in the row counts as an edit.
     * Sending the default would own the fee at $0 before any facility exists,
     * and facilities added later would never be charged.
     */
    const [serverTechRmFee, setServerTechRmFee] = useState<string | null>(null);
    const [techRmFeeEdited, setTechRmFeeEdited] = useState(false);
    const techRmFeeOverriddenLocally = serverTechRmFee === null ? techRmFeeEdited : inputs.techRmFee !== serverTechRmFee;
    /** A fee committed before the quote exists. It is unsaved work even when
     *  it equals the seeded "0" (a deliberate $0), so it joins the save
     *  signatures and the autosave picks it up on its own. */
    const techRmFeePending = serverTechRmFee === null && techRmFeeEdited;
    /** Bumped on every committed Tech/RM Fee edit. `persist` compares it
     *  across its awaits so a fee typed while a save is in flight is not
     *  overwritten by that save's response. */
    const techRmFeeEditSeq = useRef(0);
    /** Taxes & Fees writes through here so a committed Tech/RM Fee is
     *  recorded as the underwriter's edit; the page's own adoptions of the
     *  backend fee keep using `setInput` directly. */
    const setTaxesFeesInput = useCallback(
        (key: InputKey, value: string) => {
            if (key === "techRmFee") {
                setTechRmFeeEdited(true);
                techRmFeeEditSeq.current += 1;
            }
            setInput(key, value);
        },
        [setInput],
    );

    /**
     * Surplus-lines provenance (OR-1440).
     *
     * The rates the server last handed us. Updated on load and after a
     * successful InsCipher calculation, so a drift between these and the live
     * inputs means the underwriter typed over the rate by hand. That drives
     * three things: whether the PUT is allowed to send the rate columns,
     * whether we show InsCipher's amount or a rate-derived one, and (later)
     * the ✓ provenance mark.
     *
     * State rather than a ref: it's read during render to derive the override
     * flag, and it changes on discrete events (load, successful calculation) —
     * exactly the cases where a re-render is the point.
     */
    const [serverSurplusRates, setServerSurplusRates] = useState<{ taxPct: string; feePct: string; stampingPct: string } | null>(
        null,
    );

    /**
     * When InsCipher last ran, straight off the quote (OR-1440). Feeds the
     * timestamp line of the provenance tooltip.
     *
     * Null everywhere until backend PR #327 ships `tax_calculated_at` — dev
     * omits the key, so the tooltip renders title-only and the whole path is
     * inert rather than showing a wrong date. Refreshed at every point the quote
     * comes back: initial load, after a calculation, and after a save.
     */
    const [taxCalculatedAt, setTaxCalculatedAt] = useState<string | null>(null);
    /**
     * Underwriter-defined misc fee lines (OR-1494), as persisted on the quote
     * (`misc_fees`, backend OR-1421). Read here from the quote GET and refreshed
     * after every save; writes go through `/quote/misc-fees/` from the Fees
     * card. Their Σ folds into the live Final Premium so the screen matches
     * the Total Policy Cost the documents print.
     */
    const [miscFees, setMiscFees] = useState<MiscFee[]>([]);
    /**
     * Guard against stale quote refreshes clobbering `miscFees`.
     *
     * Creating or deleting a fee changes the autosave signature, so `persist`
     * (and its quote re-GET) can overlap the mutation. A GET that started before
     * a DELETE finished can resolve AFTER it, still carrying the deleted row —
     * applying it would put the fee back and the next autosave would persist a
     * Final Premium that counts it again (review on PR #474).
     *
     * Two signals decide whether a refresh may touch `miscFees`:
     *   • `miscFeesEpoch` — bumped when a mutation starts AND when it ends. A
     *     refresh remembers the epoch it started under and is discarded if the
     *     epoch moved, i.e. any mutation began or completed while it was in
     *     flight.
     *   • `miscFeeMutationsInFlight` — a refresh that resolves while a mutation
     *     is still pending is discarded too, since the backend may not have
     *     applied that mutation yet.
     * The optimistic local state stays authoritative until a refresh that
     * started after the last mutation settled comes back.
     */
    const miscFeesEpoch = useRef(0);
    const miscFeeMutationsInFlight = useRef(0);
    const applyRefreshedMiscFees = useCallback((fees: MiscFee[] | null | undefined, epochAtStart: number) => {
        if (epochAtStart !== miscFeesEpoch.current || miscFeeMutationsInFlight.current > 0) return;
        setMiscFees(fees ?? []);
    }, []);
    /**
     * Backend's own provenance flags for the persisted rates (OR-1440
     * follow-up). Plain read-only mirror of the backend, refreshed at every
     * point a fresh quote comes back — same lifecycle as `taxCalculatedAt`.
     * `null` per field until the backend ships the column; never assume
     * `false` in that case.
     */
    const [serverManuallySet, setServerManuallySet] = useState<{
        tax: boolean | null;
        fee: boolean | null;
        stamping: boolean | null;
    } | null>(null);
    // Tax, fee, and stamping are judged independently — the underwriter may
    // override just one of the three.
    //
    // No-quote path: `serverSurplusRates` stays null until a quote exists to
    // load a baseline from (first save on a brand-new submission). There's no
    // InsCipher value the input could be mirroring in that state, so ANY
    // non-empty rate is unambiguously the underwriter's own — falling back to
    // `false` here (as if untouched) used to drop a first-ever manual entry
    // from the very save meant to persist it.
    const taxRateOverriddenLocally =
        serverSurplusRates === null
            ? inputs.surplusLinesTaxPct !== ""
            : inputs.surplusLinesTaxPct !== serverSurplusRates.taxPct;
    const feeRateOverriddenLocally =
        serverSurplusRates === null
            ? inputs.surplusLinesFeePct !== ""
            : inputs.surplusLinesFeePct !== serverSurplusRates.feePct;
    const stampingRateOverriddenLocally =
        serverSurplusRates === null
            ? inputs.stampingFeePct !== ""
            : inputs.stampingFeePct !== serverSurplusRates.stampingPct;

    // Per-field provenance (OR-1440 follow-up) — combines this session's
    // live-override heuristic with the backend's own manually_set flag, when
    // the environment ships it. See `resolveSurplusLinesProvenance` for why
    // this has to be an OR, not a `??` preference chain.
    const { taxNotInsCipherAuthored, feeNotInsCipherAuthored, stampingNotInsCipherAuthored } = resolveSurplusLinesProvenance({
        taxRateOverriddenLocally,
        feeRateOverriddenLocally,
        stampingRateOverriddenLocally,
        taxManuallySetByServer: serverManuallySet?.tax ?? null,
        feeManuallySetByServer: serverManuallySet?.fee ?? null,
        stampingManuallySetByServer: serverManuallySet?.stamping ?? null,
    });

    /**
     * The persisted $ amount for each field, when it's still trustworthy as a
     * display value — i.e. the % on screen still equals the last-loaded/
     * saved % for that field (`!…RateOverriddenLocally`). In that case the
     * fetched `derived` amount IS the correct dollar figure for the rate on
     * screen, by construction (it's exactly what the last save persisted for
     * it) — whether or not InsCipher authored it, and independent of the
     * taxable-base bookkeeping below.
     *
     * Dropped (→ `undefined`, falling through to a live `rate × base`
     * recompute in `computeFees`) only while the user is actively typing an
     * unsaved new rate this session — the one case with no persisted amount
     * yet for the value on screen. Gated on the LOCAL flags only, not
     * `taxNotInsCipherAuthored`/`feeNotInsCipherAuthored` (which also fold in
     * the server's `manually_set` flag): those exist for the provenance ✓
     * below, not for this. Using them here was the bug (OR — reload/tab-nav
     * after a manual edit): a manually-set-but-unedited field would ALWAYS
     * recompute instead of trusting the just-fetched amount, and that
     * recompute silently used the wrong taxable base once
     * `taxableBaseIncludesFee` resets on reload (see its docstring below).
     */
    const persistedSurplusLinesAmounts = useMemo(
        () =>
            derived === null
                ? undefined
                : {
                      surplusLinesTax: taxRateOverriddenLocally ? undefined : derived.surplusLinesTax,
                      surplusLinesFee: feeRateOverriddenLocally ? undefined : derived.surplusLinesFee,
                      stampingFee: stampingRateOverriddenLocally ? undefined : derived.stampingFee,
                  },
        [derived, taxRateOverriddenLocally, feeRateOverriddenLocally, stampingRateOverriddenLocally],
    );

    /**
     * Whether what's on screen came from InsCipher — drives the group
     * provenance ✓ shown once above all three rate rows.
     *
     * Requires ALL THREE amounts present: tax, fee, and stamping are gated
     * independently, so an underwriter can override just one — the group ✓
     * would otherwise imply the other fields are InsCipher's too. A
     * legitimate $0 fee (TX's service charge is inactive) still passes here,
     * since these flags only go `true` when the field was actually
     * overridden (locally or per the server), not when InsCipher itself
     * returns zero.
     */
    const isInsCipherAuthored =
        !taxNotInsCipherAuthored &&
        !feeNotInsCipherAuthored &&
        !stampingNotInsCipherAuthored &&
        derived?.surplusLinesTax != null &&
        derived?.surplusLinesFee != null &&
        derived?.stampingFee != null;

    /**
     * Whether InsCipher's taxable base for Surplus Lines Tax / Fee / Stamping
     * Fee includes the Tech/RM Fee (state-specific — e.g. MO taxes
     * Premium + Broker/Tech Fee; most states tax Premium alone). PM-confirmed
     * all three fees share this ONE per-state rule.
     *
     * Both places this gets set now prefer a real per-state lookup —
     * `GET /v2/states/{code}`'s `hasTaxableFees` (OR-1440) — over guessing:
     * `onCalculateTax` keys it off the home state InsCipher just resolved
     * (`result.state`), and the initial load effect below keys it off the
     * same state once the backend persists it on the quote (`tax_state`).
     * `tax`, `fee`, and `stamping` are always set to the SAME value: all
     * three are a percentage of one state-defined taxable premium, so
     * there's one flag to look up, not three.
     *
     * The load effect still falls back to the older
     * `inferTaxableBaseIncludesFee` reverse-solve for a quote with no
     * `tax_state` on record — one calculated before that column shipped, or
     * never calculated at all. Once the backend confirms every calculated
     * quote carries `tax_state` (a backfill, or that it's been there since
     * OR-1440 launched), that fallback and the inference functions it calls
     * can be deleted.
     */
    const [taxableBaseIncludesFee, setTaxableBaseIncludesFee] = useState<{ tax: boolean; fee: boolean; stamping: boolean }>({
        tax: false,
        fee: false,
        stamping: false,
    });

    /**
     * InsCipher tax calculation (OR-1440). Single-flight: one phase value, so a
     * second press can't overlap the first. The backend also takes an advisory
     * lock, but disabling the button is what the design asks for.
     */
    const [taxCalc, setTaxCalc] = useState<{ phase: TaxCalcPhase; message?: string }>({ phase: "idle" });
    const [taxCalcFlashKey, setTaxCalcFlashKey] = useState(0);
    /** The backend rejects any status but this one with a 409. */
    const canCalculateTax = detail.status === "Indications Sent";

    const onCalculateTax = useCallback(async () => {
        if (!detail.id) return;
        setTaxCalc({ phase: "loading" });
        try {
            const result = await calculateSurplusLinesTax(detail.id);
            // Hydrate the rate inputs through the same conversion the quote
            // load uses, so the two can't disagree on rounding — and move the
            // provenance baseline with them, otherwise this write would look
            // like a manual override and drop the ✓ immediately.
            const taxPct = fractionToPctInput(result.surplus_lines_tax_rate);
            const feePct = fractionToPctInput(result.surplus_lines_fee_rate);
            const stampingPct = fractionToPctInput(result.stamping_fee_rate);
            setAndCommit("surplusLinesTaxPct", taxPct);
            setAndCommit("surplusLinesFeePct", feePct);
            setAndCommit("stampingFeePct", stampingPct);
            setServerSurplusRates({ taxPct, feePct, stampingPct });
            // Fresh InsCipher-authored trio — the taxable base is now a real
            // per-state rule (OR-1440), looked up by the home state InsCipher
            // itself resolved (`result.state`) rather than reverse-solved
            // from the filed amount/rate. Tax, fee, and stamping share ONE
            // per-state flag (`hasTaxableFees`), not inferred independently.
            const stateInfo = await getState(result.state).catch(() => null);
            setTaxableBaseIncludesFee({
                tax: stateInfo?.hasTaxableFees ?? false,
                fee: stateInfo?.hasTaxableFees ?? false,
                stamping: stateInfo?.hasTaxableFees ?? false,
            });
            // Re-GET so the persisted AMOUNTS come back — those are what the
            // card shows, not a recomputation of premium × rate.
            const refreshed = await getPremiumSelection(detail.id).catch(() => null);
            if (refreshed) {
                hydrateDerived(refreshed.derived);
                setTaxCalculatedAt(refreshed.taxCalculatedAt);
                setServerManuallySet({
                    tax: refreshed.surplusLinesTaxManuallySet,
                    fee: refreshed.surplusLinesFeeManuallySet,
                    stamping: refreshed.stampingFeeManuallySet,
                });
            }
            setTaxCalc({ phase: "done" });
            setTaxCalcFlashKey((n) => n + 1);
        } catch (error) {
            // One 409 is not a failure: the backend refuses a repeat calculation
            // when none of the fingerprinted inputs moved (premium, effective
            // date, home state, KY billing address — backend PR #327). Nothing
            // is wrong, so it gets the neutral mark instead of the red one.
            //
            // Matching on the message is the only discriminator available — the
            // other 409 is the wrong-status one and both arrive as bare 409s.
            // Sammy acknowledged the missing error code and filed it separately;
            // swap this for the code when it lands.
            if (error instanceof ApiError && error.status === 409 && /already current/i.test(error.message)) {
                setTaxCalc({ phase: "current", message: error.message });
                return;
            }
            // Everything else shows the design's copy verbatim. Backend details
            // are deliberately not surfaced — they name internal fields.
            setTaxCalc({ phase: "error" });
        }
    }, [detail.id, setAndCommit, hydrateDerived]);

    // Fees are FE-owned again (jun-25): the backend returns them null, so we
    // compute them live off the inputs + the derived Tech/RM Fee. TRIA /
    // Surplus tax / Surplus fee / Stamping fee $ amounts + Final Premium
    // drive the summary card and the Fees rows in real time AND Final Premium
    // is what we write back to the quote's `final_premium` on save. All three
    // InsCipher fees are the exception: once InsCipher has calculated, its
    // amounts win (OR-1440).
    const liveFees = useMemo(
        () => computeFees(inputs, techRmFeeAmount, persistedSurplusLinesAmounts, taxableBaseIncludesFee, sumMiscFeeAmounts(miscFees)),
        [inputs, techRmFeeAmount, persistedSurplusLinesAmounts, taxableBaseIncludesFee, miscFees],
    );
    // Overlay the live FE-owned values (per the contract the backend returns
    // them null and persists ours): the fee amounts, Final Premium, Deviation
    // from Unity and Total Commission — so the cards update on every keystroke
    // while the rest stay backend-owned.
    const derivedView = useMemo<PremiumSelectionDerived | null>(
        () =>
            derived === null
                ? null
                : {
                      ...derived,
                      tria: liveFees.tria,
                      surplusLinesTax: liveFees.surplusLinesTax,
                      surplusLinesFee: liveFees.surplusLinesFee,
                      stampingFee: liveFees.stampingFee,
                      finalPremium: liveFees.finalPremium,
                      deviationFromUnity: computeDeviationFromUnity(inputs.selectedPremium, derived.totalPremium),
                      totalCommissionPct: computeTotalCommissionPct(inputs.tricuraCommissionPct, inputs.agentCommissionPct),
                  },
        [derived, liveFees, inputs.selectedPremium, inputs.tricuraCommissionPct, inputs.agentCommissionPct],
    );

    // While a covered facility has no Retention, the backend's SIR sum leaves it
    // out, so Premium w/ SIR Credit, Adjusted Premium and Total Premium show "—"
    // instead of a partial amount (OR-1733). Display only: inputs, fees and the
    // values saved back are untouched. If the facilities fail to load, the
    // Retentions can't be checked, so those values stay hidden too.
    const hideSirValues = missingRetention > 0 || facilitiesState.status === "error";
    const derivedShown = useMemo(() => hidePartialSirValues(derived, hideSirValues), [derived, hideSirValues]);
    const derivedViewShown = useMemo(() => hidePartialSirValues(derivedView, hideSirValues), [derivedView, hideSirValues]);

    // UI-only state — no recalc participation.
    const [fortyPct, setFortyPct] = useState<Record<string, boolean>>(() => emptyCriteriaState(FORTY_PCT_CRITERIA));
    const [referral, setReferral] = useState<Record<string, boolean>>(() => emptyCriteriaState(REFERRAL_CRITERIA));
    const [referralDate, setReferralDate] = useState("");
    const [approvalDate, setApprovalDate] = useState("");
    const [approvalNotes, setApprovalNotes] = useState("");
    // Probability to Bind is a submission-level field — persist it on
    // change (optimistic), independent of the premium Save (Andressa, jun-17).
    const [probabilityToBind, setProbabilityToBind] = useState(detail.probabilityToBind || "");
    const onProbabilityToBindChange = useCallback(
        (value: string) => {
            setProbabilityToBind(value);
            if (!detail.id) return;
            // Sync the shared layout detail too — otherwise navigating away
            // and back (client-side) remounts this page and re-seeds the
            // dropdown from a STALE context value, showing the previous
            // selection until a hard reload (Henry, jun-19).
            setDetail({ ...detail, probabilityToBind: value });
            void patchSubmission(detail.id, { probability_to_bind: value || null }).catch((err) =>
                console.error("Failed to save Probability to Bind", err),
            );
        },
        [detail, setDetail],
    );

    // Load the saved Quote once per submission: hydrate the inputs +
    // criteria checks + approval dates/notes, plus the backend-computed
    // totals. 404 (no quote yet) → keep defaults; totals stay null → "—".
    useEffect(() => {
        if (!detail.id) return;
        let cancelled = false;
        const miscFeesEpochAtStart = miscFeesEpoch.current;
        void getPremiumSelection(detail.id).then(async (saved) => {
            if (cancelled) return;
            if (saved) {
                hydrate(saved.form.inputs);
                hydrateDerived(saved.derived);
                applyRefreshedMiscFees(saved.miscFees, miscFeesEpochAtStart);
                // Baseline for the surplus-lines override check (OR-1440): these
                // are the rates as persisted, so any later drift is a manual edit.
                setServerSurplusRates({
                    taxPct: saved.form.inputs.surplusLinesTaxPct,
                    feePct: saved.form.inputs.surplusLinesFeePct,
                    stampingPct: saved.form.inputs.stampingFeePct,
                });
                setTaxCalculatedAt(saved.taxCalculatedAt);
                setServerManuallySet({
                    tax: saved.surplusLinesTaxManuallySet,
                    fee: saved.surplusLinesFeeManuallySet,
                    stamping: saved.stampingFeeManuallySet,
                });
                // Tech/RM Fee baseline (backend PR #371): the fee as
                // persisted, so any later drift is the underwriter's edit.
                setServerTechRmFee(saved.form.inputs.techRmFee);
                if (saved.taxState) {
                    // Same lookup `onCalculateTax` uses, keyed by the home
                    // state InsCipher resolved and the backend now persists
                    // on the quote (`tax_state`, OR-1440 follow-up) — no more
                    // reverse-solving once a state is on record.
                    const stateInfo = await getState(saved.taxState).catch(() => null);
                    if (cancelled) return;
                    setTaxableBaseIncludesFee({
                        tax: stateInfo?.hasTaxableFees ?? false,
                        fee: stateInfo?.hasTaxableFees ?? false,
                        stamping: stateInfo?.hasTaxableFees ?? false,
                    });
                } else {
                    // Legacy fallback: a quote calculated before `tax_state`
                    // shipped (or never calculated at all) has no state to
                    // look up, so this reverse-solves the base off THIS load
                    // snapshot's own premium/fee — self-consistent with the
                    // persisted amount, and avoids racing `useFacilities` for
                    // `techRmFeeAmount`. Skipped per-field when the backend
                    // flags that field as manually set: the persisted amount
                    // is the underwriter's own past entry then, not an
                    // InsCipher-authored figure to infer from. Tax and fee
                    // share ONE per-state basis (see
                    // `inferCombinedTaxableBaseIncludesFee`) — combining
                    // means a manually-set fee (or a still-$0 one) can borrow
                    // tax's signal instead of losing the inclusive base
                    // entirely.
                    const loadedTechRmFee = Number(saved.form.inputs.techRmFee) || 0;
                    const includesFee = inferCombinedTaxableBaseIncludesFee(
                        saved.surplusLinesTaxManuallySet === true
                            ? null
                            : inferTaxableBaseIncludesFee(
                                  saved.derived.surplusLinesTax,
                                  saved.form.inputs.surplusLinesTaxPct,
                                  saved.form.inputs.selectedPremium,
                                  loadedTechRmFee,
                              ),
                        saved.surplusLinesFeeManuallySet === true
                            ? null
                            : inferTaxableBaseIncludesFee(
                                  saved.derived.surplusLinesFee,
                                  saved.form.inputs.surplusLinesFeePct,
                                  saved.form.inputs.selectedPremium,
                                  loadedTechRmFee,
                              ),
                    );
                    setTaxableBaseIncludesFee({
                        tax: includesFee,
                        fee: includesFee,
                        stamping: includesFee,
                    });
                }
                setFortyPct((prev) => ({ ...prev, ...saved.form.fortyPctCriteria }));
                setReferral((prev) => ({ ...prev, ...saved.form.referralCriteria }));
                setReferralDate(saved.form.referralDate);
                setApprovalDate(saved.form.approvalDate);
                setApprovalNotes(saved.form.approvalNotes);
            }
            // Enable auto-save only after the load settles — batched with the
            // hydrate above so the baseline is the loaded data, not the defaults.
            setLoaded(true);
        });
        return () => {
            cancelled = true;
        };
    }, [detail.id, hydrate, hydrateDerived, applyRefreshedMiscFees]);

    const next = getNextSection(SECTION_ID);
    const renewal = detail.accountBasics.renewalDate || "";
    const expiration = detail.accountBasics.expirationDate || "";
    const prereqsComplete = arePrereqSectionsComplete(detail);

    // PUT the quote + re-GET to refresh the backend-computed totals. Shared by
    // the debounced auto-save and Save & Continue (no navigation here).
    const persist = useCallback(async () => {
        // Snapshot the signature being saved so the footer's dirty flag
        // clears once this state is persisted (same shape as autosaveSignature).
        const sigFor = (feePending: boolean) =>
            JSON.stringify({ inputs, fortyPct, referral, referralDate, approvalDate, approvalNotes, miscFees: miscFeesSig(miscFees), techRmFeePending: feePending });
        const sig = sigFor(techRmFeePending);
        const techRmFeeEditSeqAtStart = techRmFeeEditSeq.current;
        await savePremiumSelection(detail.id, {
            inputs,
            fortyPctCriteria: fortyPct,
            referralCriteria: referral,
            referralDate,
            approvalDate,
            approvalNotes,
            // FE-computed Final Premium → quote.final_premium so a policy
            // can be created from this submission (Henry, jun-19).
            finalPremium: liveFees.finalPremium,
            // FE-computed Deviation from Unity → persisted so it survives reload
            // and populates the Triage "Deviation from Market" column.
            deviationFromUnity: computeDeviationFromUnity(inputs.selectedPremium, derived?.totalPremium ?? null),
            // FE-computed TRIA $ amount → quote.tria_fee_amount, always sent
            // (no InsCipher-authored figure to defer to for TRIA) so it
            // tracks the % input instead of staying frozen at a stale value.
            triaFeeAmount: liveFees.tria,
            // Same live figures the card already shows (liveFees, not a fresh
            // recompute) — only reach the wire when the matching rate is a
            // manual override, per the per-field flags below.
            surplusLinesTaxAmount: liveFees.surplusLinesTax,
            surplusLinesFeeAmount: liveFees.surplusLinesFee,
            stampingFeeAmount: liveFees.stampingFee,
        }, {
            // Tax, fee, and stamping are gated independently — overriding one
            // must not also force-send the others' untouched rate/amount and
            // risk stomping a fresher InsCipher value for a field the
            // underwriter never touched.
            includeSurplusLinesTax: taxNotInsCipherAuthored,
            includeSurplusLinesFee: feeNotInsCipherAuthored,
            includeStampingFee: stampingNotInsCipherAuthored,
            // Only when the underwriter changed it this session (backend PR #371) —
            // see `techRmFeeOverriddenLocally`.
            includeTechRmFee: techRmFeeOverriddenLocally,
        });
        // The walk is recomputed on the backend AFTER the save, so re-GET the
        // quote to pull the fresh totals.
        const miscFeesEpochAtStart = miscFeesEpoch.current;
        const refreshed = await getPremiumSelection(detail.id).catch(() => null);
        if (refreshed) {
            hydrateDerived(refreshed.derived);
            applyRefreshedMiscFees(refreshed.miscFees, miscFeesEpochAtStart);
            // The save that CREATES the quote row is where the backend seeds
            // base_rate_credit_factor = 0.15 (OR-1514). The load 404'd before
            // it, so the input is still "" here and stayed blank until a
            // reload even though Adjusted Premium already carried the 15%
            // (OR-1542). Adopt the server's value only into an input that is
            // STILL empty — checked against live state inside the hook, not
            // this callback's `inputs` snapshot, which predates the awaited
            // PUT/GET: a Base Rate typed while those were in flight wins, as
            // with every other field this refresh leaves alone.
            setInputIfEmpty("baseRateCreditPct", refreshed.form.inputs.baseRateCreditPct);
            // A manual rate edit clears the timestamp server-side, so re-read it
            // rather than assuming it survived the save (OR-1440).
            setTaxCalculatedAt(refreshed.taxCalculatedAt);
            setServerManuallySet({
                tax: refreshed.surplusLinesTaxManuallySet,
                fee: refreshed.surplusLinesFeeManuallySet,
                stamping: refreshed.stampingFeeManuallySet,
            });
            // Move the override baseline to what was just saved — otherwise a
            // manually-typed rate keeps reading as "overridden" forever, even
            // once it's the server's own persisted value.
            setServerSurplusRates({
                taxPct: refreshed.form.inputs.surplusLinesTaxPct,
                feePct: refreshed.form.inputs.surplusLinesFeePct,
                stampingPct: refreshed.form.inputs.stampingFeePct,
            });
            // The backend owns the Gemini fee's storage, so a save can come
            // back with a different one: the first save on a submission that
            // had no quote row goes from nothing to `covered facilities ×
            // rate` (OR-1501), and a hand-typed fee comes back normalised to
            // whole facilities — $32,000 is stored as $30,000 (backend PR #371; the
            // input only accepts multiples of $5,000, this catches anything
            // that got past it). Adopt it: the fee feeds Final Premium, which this
            // page computes and persists, so keeping the old value would
            // leave a total that never included the fee. The change marks
            // the page dirty and the autosave carries the corrected total,
            // converging on the next round. `setInput` no-ops when the value
            // is unchanged, so a save that did not move the fee stays clean.
            // Skipped when the underwriter committed another fee while this
            // save was in flight: theirs is newer, and with the baseline
            // below it reads as changed and rides the next autosave.
            if (techRmFeeEditSeq.current === techRmFeeEditSeqAtStart) setInput("techRmFee", refreshed.form.inputs.techRmFee);
            // Move the ownership baseline to what was just saved — otherwise
            // a typed fee keeps reading as "changed" and rides every later
            // autosave, and the adopted value above would itself count as an
            // edit (backend PR #371).
            setServerTechRmFee(refreshed.form.inputs.techRmFee);
        }
        // Once the quote exists the baseline above takes over, so a pending
        // pre-quote fee is no longer pending.
        setBaselineSig(refreshed ? sigFor(false) : sig);
    }, [
        detail.id,
        inputs,
        miscFees,
        applyRefreshedMiscFees,
        fortyPct,
        referral,
        referralDate,
        approvalDate,
        approvalNotes,
        liveFees,
        derived,
        hydrateDerived,
        setInput,
        setInputIfEmpty,
        taxNotInsCipherAuthored,
        feeNotInsCipherAuthored,
        stampingNotInsCipherAuthored,
        techRmFeeOverriddenLocally,
        techRmFeePending,
    ]);


    // Auto-save on every user edit (debounced) — Henry/Andressa jun-22, replaces
    // the manual Save button. Only when there's a real submission to PUT to;
    // the hook itself skips the initial hydrate and unchanged data.
    // ── Custom (misc) fees (OR-1494) ────────────────────────────────────
    // The quote row is the parent of misc fees, so the POST 404s until the
    // quote exists (backend OR-1421). A submission with no quote yet has
    // `derived === null` (the load 404'd) — persist first, which upserts it.
    const addCustomFees = useCallback(
        async (entries: { name: string; amount: number }[]) => {
            if (!detail.id || entries.length === 0) return;
            if (derived === null) await persist();
            // Independent rows → fire the POSTs together and fold whatever landed
            // into state in one update. Partial failures are reconciled against
            // the quote (the source of truth for what actually saved) and handed
            // back to the modal, which keeps only the failed rows for a retry.
            miscFeesEpoch.current += 1;
            miscFeeMutationsInFlight.current += 1;
            let results: PromiseSettledResult<MiscFee>[];
            try {
                results = await Promise.allSettled(entries.map((entry) => createMiscFee(detail.id, entry)));
            } finally {
                miscFeeMutationsInFlight.current -= 1;
                miscFeesEpoch.current += 1;
            }
            const created = results.flatMap((r) => (r.status === "fulfilled" ? [r.value] : []));
            if (created.length > 0) setMiscFees((prev) => [...prev, ...created]);
            const failed = entries.filter((_, i) => results[i].status === "rejected");
            if (failed.length > 0) {
                const epochAtStart = miscFeesEpoch.current;
                const refreshed = await getPremiumSelection(detail.id).catch(() => null);
                if (refreshed) applyRefreshedMiscFees(refreshed.miscFees, epochAtStart);
                throw new CustomFeeSaveError(failed);
            }
        },
        [detail.id, derived, persist, applyRefreshedMiscFees],
    );

    // Optimistic: the row leaves the card at once and comes back only if the
    // DELETE fails. The list reflects saved data, so no confirmation step.
    const removeMiscFee = useCallback(
        async (id: string) => {
            if (!detail.id) return;
            miscFeesEpoch.current += 1;
            miscFeeMutationsInFlight.current += 1;
            let removed: MiscFee | undefined;
            setMiscFees((prev) => {
                removed = prev.find((f) => f.id === id);
                return prev.filter((f) => f.id !== id);
            });
            try {
                await deleteMiscFee(detail.id, id);
            } catch {
                if (removed) setMiscFees((prev) => (prev.some((f) => f.id === id) ? prev : [...prev, removed as MiscFee]));
            } finally {
                miscFeeMutationsInFlight.current -= 1;
                miscFeesEpoch.current += 1;
            }
        },
        [detail.id],
    );

    // Misc fees ride along (OR-1494): adding or deleting one moves the live
    // Final Premium, and that figure only reaches `quote.final_premium` on
    // the next PUT — so the change has to count as something to save.
    const autosaveSignature = JSON.stringify({ inputs, fortyPct, referral, referralDate, approvalDate, approvalNotes, miscFees: miscFeesSig(miscFees), techRmFeePending });
    // Paused while a calculation is in flight (OR-1440): `onCalculateTax`
    // commits the new rate inputs (and their provenance baseline) BEFORE the
    // re-GET refreshes `derived` with the matching dollar amounts — see its
    // comments. A save that lands in that gap would compute Final Premium
    // off the new rate paired with the STALE persisted amount. Re-enabling
    // once the phase clears schedules a fresh save off the now-consistent
    // state; the debounce hook's baseline tracking (`savedSig`) means no data
    // is lost by pausing, just deferred.
    const calculatingTax = taxCalc.phase === "loading";
    useDebouncedAutosave(autosaveSignature, persist, { enabled: Boolean(detail.id) && loaded && !calculatingTax });

    // Capture the baseline once the load settles so hydrated data isn't
    // counted as dirty. After that, dirty = signature drifted from baseline.
    useEffect(() => {
        if (loaded && baselineSig === null) setBaselineSig(autosaveSignature);
    }, [loaded, baselineSig, autosaveSignature]);
    const isDirty = loaded && baselineSig !== null && autosaveSignature !== baselineSig;

    // Save persists without navigating; Continue just advances (the footer
    // guards against leaving while dirty). The debounce keeps Save mostly
    // disabled — both coexist. Also skipped mid-calculation, same reasoning
    // as pausing the debounced autosave above — a manual click in that
    // narrow window would persist the same stale-amount/new-rate mismatch.
    const onSave = useCallback(async () => {
        if (calculatingTax) return;
        setIsSaving(true);
        try {
            await persist();
        } finally {
            setIsSaving(false);
        }
    }, [persist, calculatingTax]);
    const onContinue = useCallback(() => navigate(next ? `../${next.path}` : "/submissions"), [navigate, next]);

    return (
        <>
            <div className="max-w-dashboard w-full space-y-6">
                <header className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col gap-1">
                        <h2 className="text-primary text-md font-semibold">Premium Selection</h2>
                        <p className="text-tertiary text-sm">
                            Calculate and select the primary premium, coverages, and final binding amount
                        </p>
                    </div>
                    <div className="flex w-full items-stretch justify-between gap-6 md:w-auto md:shrink-0 md:justify-start">
                        <DateField label="Renewal Date" value={renewal} />
                        <span aria-hidden="true" className="bg-border-secondary hidden w-px self-stretch md:block" />
                        <DateField label="Expiration Date" value={expiration} />
                    </div>
                </header>

                {!prereqsComplete && (
                    <FeaturedAlert
                        color="warning"
                        title="Complete required sections before generating a Premium"
                        description="Make sure the following sections are filled out: UW Information, Schedule, and Loss Runs."
                    />
                )}

                {/* Archived facilities aren't priced, so say so before the
                  * underwriter reads the premium below as covering the whole
                  * account (OR-1431). Stacks under the incomplete-sections
                  * alert rather than replacing it — both can be true. */}
                {archivedFacilities > 0 && !archivedAlertDismissed && (
                    <FeaturedAlert
                        color="warning"
                        title={`This policy covers ${coveredFacilities} of ${totalFacilities} facilities`}
                        description={`${archivedFacilities} ${
                            archivedFacilities === 1 ? "facility is" : "facilities are"
                        } excluded from coverage and the premium calculation below. Restore ${
                            archivedFacilities === 1 ? "it" : "them"
                        } to bring ${archivedFacilities === 1 ? "it" : "them"} back`}
                        primaryAction={{
                            label: "Check Schedule Page",
                            iconTrailing: ArrowRight,
                            // Absolute, matching the identical "Check Schedule
                            // Page" CTA in loss-runs-table.tsx — this page sits
                            // at `calculations/premium-selection`, so a
                            // relative hop is easy to get wrong.
                            onClick: () => navigate(`/submissions/${detail.id}/schedule`),
                        }}
                        onClose={() => setArchivedAlertDismissed(true)}
                    />
                )}

                {/* A blank Retention is never priced as $0 (an explicit $0 is a
                  * real retention), so send the underwriter back to set it.
                  * Not dismissible: it explains the "—" values below (OR-1733). */}
                {missingRetention > 0 && (
                    <FeaturedAlert
                        color="warning"
                        title="Set a retention for every facility to calculate the Adjusted Premium"
                        description={`${missingRetention} ${
                            missingRetention === 1 ? "facility is" : "facilities are"
                        } missing a Retention on the Schedule page. Go back and set a Retention so the Adjusted Premium can populate.`}
                        primaryAction={{
                            label: "Check Schedule Page",
                            iconTrailing: ArrowRight,
                            onClick: () => navigate(`/submissions/${detail.id}/schedule`),
                        }}
                        data-autotest="premium-selection-missing-retention-alert"
                    />
                )}

                <PremiumSummaryCards
                    derived={derivedViewShown}
                    pending={pending}
                    selectedPremium={inputs.selectedPremium}
                    sir={inputs.sir}
                    expiringPremium={detail.accountBasics.expiringPremium}
                    expiringSir={detail.accountBasics.expiringDeductible}
                />

                <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
                    {/* Left column = active inputs/calculations; right
                     * column = static reference data (per Henry). */}
                    <div className="flex flex-col gap-6">
                        <PrimaryPremiumWalkCard
                            inputs={inputs}
                            derived={derivedShown}
                            pending={pending}
                            setInput={setInput}
                            setAndCommit={setAndCommit}
                            commit={commit}
                        />
                        <AdditionalCoveragesCard inputs={inputs} derived={derivedShown} pending={pending} setInput={setInput} commit={commit} />
                        <CommissionsCard inputs={inputs} derived={derivedView} pending={pending} setInput={setInput} commit={commit} />
                        <TaxesFeesCard
                            inputs={inputs}
                            derived={derivedView}
                            setInput={setTaxesFeesInput}
                            commit={commit}
                            canCalculateTax={canCalculateTax}
                            taxCalcPhase={taxCalc.phase}
                            taxCalcCurrentMessage={taxCalc.message}
                            isInsCipherAuthored={isInsCipherAuthored}
                            taxCalculatedAt={taxCalculatedAt}
                            taxCalcFlashKey={taxCalcFlashKey}
                            onCalculateTax={onCalculateTax}
                            miscFees={miscFees}
                            onAddCustomFees={addCustomFees}
                            onDeleteMiscFee={(id) => void removeMiscFee(id)}
                        />
                        <PremiumCard
                            inputs={inputs}
                            derived={derivedView}
                            pending={pending}
                            setInput={setInput}
                            commit={commit}
                            probabilityToBind={probabilityToBind}
                            onProbabilityToBindChange={onProbabilityToBindChange}
                        />
                        {/* Dec sub-limits (OR-1455). They belong with Policy
                          * Terms conceptually, but the right column already runs
                          * much longer than the left — putting them there left a
                          * dead gap under Premium. Parked at the foot of the left
                          * column instead, which balances the two. */}
                        <CoverageBCard inputs={inputs} setInput={setInput} />
                        <CoverageCCard inputs={inputs} setInput={setInput} />
                    </div>
                    <div className="flex flex-col gap-6">
                        <PolicyTermsCard inputs={inputs} setInput={setInput} />
                        <SirCreditsCard />
                        <CheckboxListCard
                            title="40% Criteria"
                            icon={Clipboard}
                            items={FORTY_PCT_CRITERIA}
                            checked={fortyPct}
                            onToggle={(id, next) => setFortyPct((prev) => ({ ...prev, [id]: next }))}
                            data-autotest="premium-selection-forty-pct-criteria-card"
                        />
                        <CheckboxListCard
                            title="Referral Criteria"
                            icon={List}
                            items={REFERRAL_CRITERIA}
                            checked={referral}
                            onToggle={(id, next) => setReferral((prev) => ({ ...prev, [id]: next }))}
                            data-autotest="premium-selection-referral-criteria-card"
                        />
                        <ReferralApprovalDatesCard
                            referralDate={referralDate}
                            approvalDate={approvalDate}
                            onReferralDateChange={setReferralDate}
                            onApprovalDateChange={setApprovalDate}
                        />
                        {/* Approval Notes closes the right column per the
                          * approved layout (Andressa, 4-ago) — it used to run
                          * full-width below the grid. */}
                        <ApprovalNotesCard notes={approvalNotes} onNotesChange={setApprovalNotes} />
                    </div>
                </div>
            </div>

            <SubmissionFooter
                onSave={onSave}
                onContinue={onContinue}
                isDirty={isDirty}
                isSaving={isSaving}
                nextSectionLabel={next?.label ?? null}
            />
        </>
    );
}

function DateField({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center gap-1.5">
            <span className="text-tertiary text-xs font-normal">{label}</span>
            <span className="text-secondary inline-flex items-center gap-1.5 text-xs font-semibold">
                <Calendar aria-hidden="true" className="text-fg-quaternary size-4" />
                {value || <span className="text-placeholder font-normal">—</span>}
            </span>
        </div>
    );
}
